import React, { useState } from 'react';
import { periodTotals } from './tripTotals';
import JunctionMatrices from './JunctionMatrices';
import { defaultPurposeSplit, normaliseSplit, EDUCATION_CAR_PCT } from './tripPurpose';

// Read-only "outputs at a glance" page: trip generation (per scenario),
// mode share, and trip distribution. Everything is derived from data the app
// already computes — trip totals from scenarios, mode split from modeShare /
// manualModes, distribution from odFlows. Nothing new is calculated here.

const PU = '#3D1A6E', PU2 = '#7B4DBF', PU_SOFT = '#F5F0FA', PU_LINE = '#EDE5F8';
const CREAM = '#F6F1E9', INK = '#1C1C2E', MUT = '#6B6880', TEAL = '#0D9488';

// Palette for the mode-share bar. Car driver (census code 7) is always the
// strong purple; the rest cycle through supporting tones.
const MODE_COLOURS = ['#0D9488', '#7B4DBF', '#378ADD', '#BA7517', '#D85A30', '#0F6E56', '#C4BDD4'];

function Card({ title, right, children, style }) {
  return (
    <div style={{ background: '#fff', border: `1.5px solid ${PU_LINE}`, borderRadius: 13, padding: '16px 18px', ...style }}>
      {title && (
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.07em', textTransform: 'uppercase', color: PU }}>{title}</div>
          {right && <div style={{ marginLeft: 'auto' }}>{right}</div>}
        </div>
      )}
      {children}
    </div>
  );
}

function Empty({ children }) {
  return <div style={{ fontSize: 12.5, color: MUT, lineHeight: 1.5 }}>{children}</div>;
}

function Stat({ label, value, color = PU }) {
  return (
    <div>
      <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: MUT }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    </div>
  );
}

function LegendCount({ c, label, n }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12 }}>
      <span style={{ width: 11, height: 11, borderRadius: 3, background: c, flexShrink: 0 }} />
      <span style={{ color: MUT }}>{label}</span>
      <span style={{ fontWeight: 800, color: INK, fontVariantNumeric: 'tabular-nums' }}>{n}</span>
    </div>
  );
}

// Junction flow-scenario growth factors are keyed by time-period id, but the
// junction/assignment view works in am/pm terms — so map am/pm to a time period
// by name (falling back to order) and return that period's id.
function periodIdFor(assignPeriod, timePeriods) {
  const key = assignPeriod === 'pm' ? 'pm' : 'am'; // 'both' → am
  const byName = timePeriods.find(tp => (tp.name || '').toLowerCase().includes(key));
  const tp = byName || timePeriods[key === 'am' ? 0 : 1];
  return tp ? tp.id : null;
}

// The purpose split ({employment,education,other}%) for a scenario+period,
// honouring "same trip purpose as Scenario 1" and the NTS band, else the default.
function purposeSplitOf(sc, tp, scenarios) {
  const s1 = scenarios[0];
  const src = (sc && s1 && sc !== s1 && sc.sameTripPurpose) ? s1 : sc;
  const band = (src && src.purposeBand) || 'hour';
  const stored = tp && src && src.tripPurpose && src.tripPurpose[tp.id];
  return normaliseSplit(stored || defaultPurposeSplit(tp ? tp.startTime : '', band));
}

// A three-part stacked bar for the purpose split, with a legend of counts.
function PurposeBar({ emp, edu, oth }) {
  const total = emp + edu + oth || 1;
  const parts = [
    { key: 'Employment', n: emp, c: '#3D1A6E' },
    { key: 'Education', n: edu, c: '#0D9488' },
    { key: 'Other', n: oth, c: '#7B4DBF' },
  ];
  return (
    <>
      <div style={{ display: 'flex', height: 22, borderRadius: 6, overflow: 'hidden', marginBottom: 9 }}>
        {parts.map(p => p.n > 0 && <div key={p.key} title={`${p.key} ${p.n}`} style={{ width: `${(p.n / total) * 100}%`, background: p.c }} />)}
      </div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        {parts.map(p => (
          <div key={p.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: p.c, flexShrink: 0 }} />
            <span style={{ color: '#6B6880' }}>{p.key}</span>
            <span style={{ fontWeight: 800, color: '#1C1C2E', fontVariantNumeric: 'tabular-nums' }}>{p.n}</span>
            <span style={{ color: '#C4BDD4', fontVariantNumeric: 'tabular-nums' }}>({Math.round((p.n / total) * 100)}%)</span>
          </div>
        ))}
      </div>
    </>
  );
}

export default function ResultsSummary({
  projectName, scenarios = [], timePeriods = [], devSize,
  modeShare, manualModes = [], modeShareMethod = 'census', odFlows,
  educationSchools, assignedNetwork,
  junctionMatrices, assignPeriod = 'am', existingFlows, onSetExisting, onPasteExisting,
  flowScenarios = [], onChangeFlowScenarios,
  onExportPDF, onExportExcel, exportGenerating,
  onBackToOverview, onOpenMap,
}) {
  const [subTab, setSubTab] = useState('summary');
  const [activeScId, setActiveScId] = useState(scenarios[0] ? scenarios[0].id : null);
  const activeSc = scenarios.find(s => s.id === activeScId) || scenarios[0];
  const periods = timePeriods.length > 0 ? timePeriods : [];

  const modes = ((modeShareMethod === 'manual' ? manualModes : (modeShare && modeShare.modes)) || [])
    .map(m => ({ ...m, pct: parseFloat(m.percentage) || 0 }))
    .filter(m => m.pct > 0)
    .sort((a, b) => b.pct - a.pct);
  const modeTotal = modes.reduce((s, m) => s + m.pct, 0) || 1;

  const flows = (odFlows && odFlows.flows) ? [...odFlows.flows].sort((a, b) => (b.percentage || 0) - (a.percentage || 0)) : [];
  const topFlows = flows.slice(0, 8);

  return (
    <div style={{ minHeight: '100vh', background: CREAM, fontFamily: 'Nunito Sans, Avenir, sans-serif' }}>

      {/* Top bar */}
      <div style={{ height: 54, background: '#fff', borderBottom: `1.5px solid ${PU_LINE}`, borderLeft: `4px solid ${PU}`, display: 'flex', alignItems: 'center', gap: 12, padding: '0 16px' }}>
        <button onClick={onBackToOverview} style={{ background: 'none', border: `1.5px solid ${PU_LINE}`, borderRadius: 7, color: PU, fontSize: 11, fontWeight: 800, padding: '7px 11px', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>← Overview</button>
        <div style={{ width: 1, height: 24, background: PU_LINE }} />
        <div style={{ fontSize: 15, fontWeight: 800, color: PU, display: 'flex', alignItems: 'center', gap: 8 }}>📋 Results summary</div>
        <div style={{ fontSize: 12, fontWeight: 600, color: MUT, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>· {projectName}</div>
        <div style={{ flex: 1 }} />
        {onExportPDF && (
          <button onClick={onExportPDF} disabled={exportGenerating}
            style={{ background: '#fff', color: PU, border: `1.5px solid ${PU_LINE}`, borderRadius: 7, padding: '8px 12px', fontSize: 11, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', cursor: exportGenerating ? 'not-allowed' : 'pointer', fontFamily: 'inherit', opacity: exportGenerating ? 0.5 : 1 }}>
            📄 PDF
          </button>
        )}
        {onExportExcel && (
          <button onClick={onExportExcel}
            style={{ background: '#fff', color: TEAL, border: `1.5px solid ${TEAL}`, borderRadius: 7, padding: '8px 12px', fontSize: 11, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'inherit' }}>
            📊 Excel
          </button>
        )}
        <button onClick={onOpenMap} style={{ background: PU, color: '#fff', border: 'none', borderRadius: 7, padding: '8px 14px', fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'inherit' }}>Open map →</button>
      </div>

      <div style={{ maxWidth: 940, margin: '0 auto', padding: '20px 22px 50px', display: 'flex', flexDirection: 'column', gap: 15 }}>

        {/* Sub-tabs */}
        <div style={{ display: 'flex', gap: 3, background: PU_SOFT, borderRadius: 8, padding: 3, width: 'fit-content' }}>
          {[['summary', 'Summary'], ['junctions', 'Junction matrices']].map(([k, lbl]) => (
            <button key={k} onClick={() => setSubTab(k)}
              style={{ border: 'none', background: subTab === k ? '#fff' : 'transparent', color: subTab === k ? PU : MUT, fontSize: 11.5, fontWeight: 700, padding: '7px 14px', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit', boxShadow: subTab === k ? '0 1px 4px rgba(61,26,110,0.14)' : 'none' }}>
              {lbl}
            </button>
          ))}
        </div>

        {subTab === 'junctions' && (
          <JunctionMatrices
            junctionMatrices={junctionMatrices}
            period={assignPeriod}
            projectName={projectName}
            existingFlows={existingFlows}
            onSetExisting={onSetExisting}
            onPasteExisting={onPasteExisting}
            flowScenarios={flowScenarios}
            onChangeFlowScenarios={onChangeFlowScenarios}
            timePeriods={periods}
            activePeriodId={periodIdFor(assignPeriod, periods)}
          />
        )}

        {subTab === 'summary' && <>

        {/* Scenario selector */}
        {scenarios.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: MUT }}>Scenario</span>
            <select value={activeScId || ''} onChange={e => setActiveScId(e.target.value)}
              style={{ padding: '7px 11px', borderRadius: 7, border: `1.5px solid ${PU_LINE}`, background: '#fff', color: INK, fontWeight: 700, fontSize: 12, fontFamily: 'inherit', cursor: 'pointer', outline: 'none' }}>
              {scenarios.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        )}

        {/* Trip generation */}
        <Card title={`Trip generation${activeSc ? ' — ' + (activeSc.name || 'Scenario') : ''}`}>
          {(!activeSc || periods.length === 0) ? (
            <Empty>No scenarios or time periods yet. Set them up in the map workspace to see trip generation here.</Empty>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(periods.length, 3)}, 1fr)`, gap: 12 }}>
              {periods.map(tp => {
                const t = periodTotals(activeSc, tp, scenarios, devSize);
                return (
                  <div key={tp.id} style={{ background: PU_SOFT, borderRadius: 10, padding: '13px 15px' }}>
                    <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: PU2 }}>{tp.name}</div>
                    <div style={{ fontSize: 24, fontWeight: 800, color: PU, margin: '4px 0 6px', fontVariantNumeric: 'tabular-nums' }}>{t.total} <span style={{ fontSize: 11, fontWeight: 700, color: MUT }}>trips/hr</span></div>
                    <div style={{ fontSize: 11.5, color: MUT, fontVariantNumeric: 'tabular-nums' }}>{t.arr} arrivals · {t.dep} departures</div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* Trip purpose split */}
        <Card title="Trip purpose split" right={<span style={{ fontSize: 10.5, fontWeight: 700, color: MUT }}>NTS 2024</span>}>
          {(!activeSc || periods.length === 0) ? (
            <Empty>Set trip purpose in the map workspace to see the Employment / Education / Other split.</Empty>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(periods.length, 3)}, 1fr)`, gap: 16 }}>
              {periods.map(tp => {
                const t = periodTotals(activeSc, tp, scenarios, devSize);
                const sp = purposeSplitOf(activeSc, tp, scenarios);
                const emp = Math.round(t.total * sp.employment / 100);
                const edu = Math.round(t.total * sp.education / 100);
                const oth = t.total - emp - edu;
                return (
                  <div key={tp.id}>
                    <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: PU2, marginBottom: 8 }}>{tp.name}</div>
                    <PurposeBar emp={emp} edu={edu} oth={oth} />
                  </div>
                );
              })}
            </div>
          )}
          <div style={{ fontSize: 11, color: MUT, marginTop: 12, lineHeight: 1.5 }}>
            Education is distributed to local schools (gravity model); Employment &amp; Other follow the census journey-to-work distribution.
          </div>
        </Card>

        {/* Mode share */}
        <Card title="Mode share" right={<span style={{ fontSize: 10.5, fontWeight: 700, color: MUT }}>{modeShareMethod === 'manual' ? 'Manual' : 'Census 2021'}</span>}>
          {modes.length === 0 ? (
            <Empty>Mode share not calculated yet. Run it from the Census &amp; Assignment tab in the map workspace.</Empty>
          ) : (
            <>
              <div style={{ display: 'flex', height: 26, borderRadius: 7, overflow: 'hidden', marginBottom: 13 }}>
                {modes.map((m, i) => (
                  <div key={m.code} title={`${m.name} ${Math.round(m.pct)}%`}
                    style={{ width: `${(m.pct / modeTotal) * 100}%`, background: m.code === 7 ? PU : MODE_COLOURS[i % MODE_COLOURS.length] }} />
                ))}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '7px 18px' }}>
                {modes.map((m, i) => (
                  <div key={m.code} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                    <span style={{ width: 11, height: 11, borderRadius: 3, flexShrink: 0, background: m.code === 7 ? PU : MODE_COLOURS[i % MODE_COLOURS.length] }} />
                    <span style={{ color: MUT, flex: 1 }}>{m.name}</span>
                    <span style={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{Math.round(m.pct)}%</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </Card>

        {/* Distribution */}
        <Card title="Trip distribution" right={odFlows ? <span style={{ fontSize: 10.5, fontWeight: 700, color: MUT }}>{flows.length} zones · top {topFlows.length}</span> : null}>
          {topFlows.length === 0 ? (
            <Empty>No distribution yet. Select output areas and generate OD flows in the map workspace.</Empty>
          ) : (
            <>
              {topFlows.map((f, i) => (
                <div key={f.destination || i} style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 9 }}>
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: INK, width: 190, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name || f.destination}</span>
                  <span style={{ flex: 1, height: 10, background: PU_SOFT, borderRadius: 5, overflow: 'hidden' }}>
                    <span style={{ display: 'block', height: '100%', borderRadius: 5, background: f.type === 'lsoa' ? TEAL : PU2, width: `${Math.min(f.percentage || 0, 100)}%` }} />
                  </span>
                  <span style={{ fontSize: 11.5, fontWeight: 800, color: PU, width: 48, textAlign: 'right', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{Math.round((f.percentage || 0) * 10) / 10}%</span>
                </div>
              ))}
              <div style={{ fontSize: 11, color: MUT, marginTop: 10, lineHeight: 1.5 }}>
                {odFlows && odFlows.total_trips ? `${odFlows.total_trips} census commuters from selected output areas.` : ''} Distribution is project-level; it doesn't vary by scenario.
              </div>
            </>
          )}
        </Card>

        {/* Education distribution — schools (gravity model) */}
        {educationSchools && educationSchools.schools && educationSchools.schools.filter(s => (s.trips || 0) > 0).length > 0 && (() => {
          const schools = educationSchools.schools.filter(s => (s.trips || 0) > 0);
          const eduPersons = educationSchools.edu_trips || schools.reduce((a, x) => a + (x.trips || 0), 0);
          const eduVeh = Math.round(eduPersons * EDUCATION_CAR_PCT / 100);
          const top = schools.slice(0, 6);
          return (
            <Card title="Education distribution — schools" right={<span style={{ fontSize: 10.5, fontWeight: 700, color: TEAL }}>Gravity model</span>}>
              <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap', marginBottom: 15 }}>
                <Stat label="Education trips" value={eduPersons} color={TEAL} />
                <Stat label="Schools" value={schools.length} color={TEAL} />
                <Stat label="Mean trip" value={`${educationSchools.mean_trip_km} km`} color={TEAL} />
                <Stat label={`Car (${EDUCATION_CAR_PCT}%)`} value={`${eduVeh} veh`} color={TEAL} />
              </div>
              {top.map((s, i) => (
                <div key={s.urn || i} style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 8 }}>
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: INK, width: 200, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</span>
                  <span style={{ fontSize: 10, color: MUT, width: 66, flexShrink: 0, textTransform: 'capitalize' }}>{(s.phase || '').replace('_', ' ')}</span>
                  <span style={{ flex: 1, height: 10, background: PU_SOFT, borderRadius: 5, overflow: 'hidden' }}>
                    <span style={{ display: 'block', height: '100%', borderRadius: 5, background: TEAL, width: `${Math.min((s.share || 0) * 100, 100)}%` }} />
                  </span>
                  <span style={{ fontSize: 11.5, fontWeight: 800, color: TEAL, width: 58, textAlign: 'right', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{s.trips} trips</span>
                </div>
              ))}
              <div style={{ fontSize: 11, color: MUT, marginTop: 8, lineHeight: 1.5 }}>Top {top.length} of {schools.length} schools receiving trips. Pupil-weighted and distance-decayed; primary and secondary distributed separately.</div>
            </Card>
          );
        })()}

        {/* Assignment outcome — how the vehicle trips split across the network */}
        {assignedNetwork && assignedNetwork.routes && assignedNetwork.routes.features && assignedNetwork.routes.features.length > 0 && (() => {
          const feats = assignedNetwork.routes.features;
          const isSchool = d => typeof d === 'string' && d.startsWith('SCH');
          const sum = pred => feats.filter(f => pred(f.properties.destination)).reduce((a, f) => a + (f.properties.trips || 0), 0);
          const schoolVeh = sum(isSchool);
          const censusVeh = sum(d => !isSchool(d));
          const total = schoolVeh + censusVeh || 1;
          return (
            <Card title="Assignment outcome — vehicle trips on the network" right={<span style={{ fontSize: 10.5, fontWeight: 700, color: MUT }}>{String(assignPeriod).toUpperCase()} peak</span>}>
              <div style={{ display: 'flex', height: 22, borderRadius: 6, overflow: 'hidden', marginBottom: 11 }}>
                {censusVeh > 0 && <div title={`Employment + Other ${censusVeh}`} style={{ width: `${(censusVeh / total) * 100}%`, background: PU }} />}
                {schoolVeh > 0 && <div title={`Education ${schoolVeh}`} style={{ width: `${(schoolVeh / total) * 100}%`, background: TEAL }} />}
              </div>
              <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                <LegendCount c={PU} label="Employment + Other → census zones" n={censusVeh} />
                <LegendCount c={TEAL} label="Education → schools" n={schoolVeh} />
                <LegendCount c={INK} label="Total assigned" n={schoolVeh + censusVeh} />
              </div>
              <div style={{ fontSize: 11, color: MUT, marginTop: 12, lineHeight: 1.5 }}>
                Person trips were split by purpose; Education was distributed to schools and the rest across census zones, each converted to car trips at its own mode share and routed on the same network. Education used a {EDUCATION_CAR_PCT}% car share.
              </div>
            </Card>
          );
        })()}

        </>}

      </div>
    </div>
  );
}
